import React from 'react';
import { Link } from 'react-router-dom';
import { ShieldCheckIcon, ArrowLeftIcon } from '@heroicons/react/24/outline';

const PrivacyPolicy = () => {
  return (
    <div className="flex flex-col flex-1 min-h-0 overflow-hidden bg-gray-50">
      {/* Header - fixed */}
      <div className="flex-shrink-0 bg-white shadow-sm">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <Link 
            to="/" 
            className="inline-flex items-center text-sm text-gray-600 hover:text-gray-900 mb-4"
          >
            <ArrowLeftIcon className="w-4 h-4 mr-2" />
            Back to Home
          </Link>
          <div className="flex items-center gap-3">
            <ShieldCheckIcon className="w-8 h-8 text-blue-600" />
            <h1 className="text-3xl font-bold text-gray-900">Privacy Policy</h1>
          </div>
          <p className="text-gray-600 mt-2">Last updated: February 2026</p>
        </div>
      </div>

      {/* Content - scrolls inside viewport */}
      <div className="flex-1 min-h-0 overflow-y-auto" data-app-scroll-root>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white rounded-lg shadow-sm p-8 prose prose-lg max-w-none">
          
          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Overview</h2>
            <p className="text-gray-700">
              SmilesFlow ("we", "our", or "us") is committed to protecting your privacy. This Privacy Policy 
              explains how we collect, use, disclose, and safeguard your information when you use our 
              interactive game platform.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">1. Information We Collect</h2>
            
            <h3 className="text-lg font-medium text-gray-800 mt-4 mb-2">1.1 Account Information</h3>
            <ul className="space-y-2 text-gray-700">
              <li>• Email address (required for account creation)</li>
              <li>• Name (provided during registration)</li>
              <li>• Company/organization name</li>
              <li>• Payment information (processed securely by Stripe)</li>
            </ul>
            
            <h3 className="text-lg font-medium text-gray-800 mt-4 mb-2">1.2 Game & Analytics Data</h3>
            <ul className="space-y-2 text-gray-700">
              <li>• Game configurations and settings you create</li>
              <li>• Play events (game starts, completions, scores)</li>
              <li>• CTA clicks and share events</li>
              <li>• Device and browser information (for analytics)</li>
            </ul>
            
            <h3 className="text-lg font-medium text-gray-800 mt-4 mb-2">1.3 Leaderboard & Lead Data</h3>
            <ul className="space-y-2 text-gray-700">
              <li>• Player names (when voluntarily submitted to leaderboards)</li>
              <li>• Player email addresses (when email capture is enabled by you)</li>
              <li>• Marketing consent preferences</li>
              <li>• Scores and timestamps</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">2. How We Use Your Information</h2>
            <ul className="space-y-2 text-gray-700">
              <li>• <strong>Service delivery:</strong> To provide, operate, and maintain the platform</li>
              <li>• <strong>Account management:</strong> To manage your account and subscription</li>
              <li>• <strong>Analytics:</strong> To provide usage statistics and insights</li>
              <li>• <strong>Communication:</strong> To send service updates and important notices</li>
              <li>• <strong>Support:</strong> To respond to your inquiries and provide customer service</li>
              <li>• <strong>Security:</strong> To detect and prevent fraud, abuse, and security issues</li>
              <li>• <strong>Legal compliance:</strong> To comply with applicable laws and regulations</li>
            </ul>
          </section>

          <section className="mb-8 bg-amber-50 rounded-lg p-6 border border-amber-200">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">3. Data Retention</h2>
            <p className="text-gray-700 mb-4">
              We retain your data according to the following schedule:
            </p>
            
            <h3 className="text-lg font-medium text-gray-800 mt-4 mb-2">Active Accounts (Trial & Paid)</h3>
            <ul className="space-y-2 text-gray-700 mb-4">
              <li>• <strong>Account data:</strong> Retained while your account is active</li>
              <li>• <strong>Analytics & events:</strong> 90 days rolling window</li>
              <li>• <strong>Leaderboard entries:</strong> 90 days rolling window</li>
              <li>• <strong>Game configurations:</strong> Retained while account is active</li>
            </ul>
            
            <h3 className="text-lg font-medium text-gray-800 mt-4 mb-2">Paused/Canceled Accounts</h3>
            <ul className="space-y-2 text-gray-700 mb-4">
              <li>• <strong>7-day grace period:</strong> All data retained for 7 days after subscription ends</li>
              <li>• <strong>After grace period:</strong> All game data, analytics, and leaderboard entries are permanently deleted</li>
              <li>• <strong>Account record:</strong> Basic account information retained for legal/tax purposes (up to 7 years)</li>
            </ul>
            
            <h3 className="text-lg font-medium text-gray-800 mt-4 mb-2">Player Data (Leaderboard Entries)</h3>
            <ul className="space-y-2 text-gray-700">
              <li>• Player names and scores: 90 days for active accounts</li>
              <li>• Player email addresses: Deleted when leaderboard is reset or account expires</li>
              <li>• Marketing consent: Deleted along with email address</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">4. Marketing & Consent</h2>
            <p className="text-gray-700 mb-4">
              When you enable email capture on your games:
            </p>
            <ul className="space-y-2 text-gray-700">
              <li>• Players must voluntarily submit their email to join the leaderboard</li>
              <li>• Marketing consent is captured separately via an unchecked opt-in checkbox</li>
              <li>• You are responsible for honoring players' marketing preferences</li>
              <li>• We do not use player email addresses for our own marketing</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">5. Third-Party Services</h2>
            <p className="text-gray-700 mb-4">We use the following trusted subprocessors:</p>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm text-gray-700">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-2 font-semibold">Service</th>
                    <th className="text-left py-2 font-semibold">Purpose</th>
                    <th className="text-left py-2 font-semibold">Data Processed</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b">
                    <td className="py-2">Firebase (Google)</td>
                    <td className="py-2">Database & Authentication</td>
                    <td className="py-2">Account data, game data</td>
                  </tr>
                  <tr className="border-b">
                    <td className="py-2">Stripe</td>
                    <td className="py-2">Payment processing</td>
                    <td className="py-2">Payment information</td>
                  </tr>
                  <tr className="border-b">
                    <td className="py-2">Postmark</td>
                    <td className="py-2">Transactional email</td>
                    <td className="py-2">Email addresses</td>
                  </tr>
                  <tr className="border-b">
                    <td className="py-2">Google Analytics</td>
                    <td className="py-2">Usage analytics</td>
                    <td className="py-2">Anonymous usage data</td>
                  </tr>
                  <tr>
                    <td className="py-2">reCAPTCHA</td>
                    <td className="py-2">Spam protection</td>
                    <td className="py-2">Device/browser info</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">6. Cookies & Tracking</h2>
            <p className="text-gray-700 mb-4">We use the following types of cookies:</p>
            <ul className="space-y-2 text-gray-700">
              <li>• <strong>Necessary cookies:</strong> Required for the site to function (authentication, security)</li>
              <li>• <strong>Analytics cookies:</strong> Help us understand how you use the platform (opt-in via cookie banner)</li>
              <li>• <strong>Marketing cookies:</strong> Used to measure advertising effectiveness (opt-in via cookie banner)</li>
            </ul>
            <p className="text-gray-700 mt-4">
              You can manage your cookie preferences through our cookie consent banner displayed on your first visit.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">7. Your Rights (GDPR)</h2>
            <p className="text-gray-700 mb-4">If you are in the European Economic Area, you have the right to:</p>
            <ul className="space-y-2 text-gray-700">
              <li>• <strong>Access:</strong> Request a copy of your personal data</li>
              <li>• <strong>Rectification:</strong> Request correction of inaccurate data</li>
              <li>• <strong>Erasure:</strong> Request deletion of your data ("right to be forgotten")</li>
              <li>• <strong>Portability:</strong> Request your data in a machine-readable format</li>
              <li>• <strong>Restriction:</strong> Request limitation of processing</li>
              <li>• <strong>Object:</strong> Object to processing based on legitimate interests</li>
              <li>• <strong>Withdraw consent:</strong> Withdraw consent for marketing or analytics at any time</li>
            </ul>
            <p className="text-gray-700 mt-4">
              To exercise these rights, contact us at{' '}
              <a href="mailto:support@smilesflow.com" className="text-blue-600 hover:underline">
                support@smilesflow.com
              </a>
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">8. Data Security</h2>
            <p className="text-gray-700 mb-4">
              We implement industry-standard security measures to protect your data:
            </p>
            <ul className="space-y-2 text-gray-700">
              <li>• All data is encrypted in transit (HTTPS/TLS)</li>
              <li>• Data is encrypted at rest in Firebase's secure infrastructure</li>
              <li>• Payment data is handled by PCI-compliant Stripe</li>
              <li>• Regular security audits and monitoring</li>
              <li>• Access controls and authentication requirements</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">9. International Transfers</h2>
            <p className="text-gray-700">
              Your data may be processed in countries outside your residence, including the United States 
              (where our subprocessors operate). We ensure appropriate safeguards are in place through 
              standard contractual clauses and adequacy decisions where applicable.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">10. Children's Privacy</h2>
            <p className="text-gray-700">
              Our Service is not intended for children under 13 (or 16 in the EEA). We do not knowingly 
              collect personal information from children. If you believe a child has provided us with 
              personal information, please contact us immediately.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">11. Changes to This Policy</h2>
            <p className="text-gray-700">
              We may update this Privacy Policy from time to time. We will notify you of significant 
              changes by email or through a prominent notice on our platform. Your continued use of 
              the Service after changes constitutes acceptance of the updated policy.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">12. Contact Us</h2>
            <p className="text-gray-700 mb-4">
              For privacy-related inquiries or to exercise your rights, contact us:
            </p>
            <ul className="space-y-2 text-gray-700">
              <li>• <strong>Email:</strong>{' '}
                <a href="mailto:support@smilesflow.com" className="text-blue-600 hover:underline">
                  support@smilesflow.com
                </a>
              </li>
              <li>• <strong>Data Controller:</strong> SmilesFlow</li>
            </ul>
          </section>

          <div className="border-t pt-6 mt-8">
            <p className="text-sm text-gray-500">
              This privacy policy is effective as of February 2026 and will remain in effect until changed.
            </p>
          </div>
        </div>
      </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
